import { useCallback, useEffect, useState } from "react";
import { Calculator, History, Layers, Loader2, MapPin, BarChart3, Settings2, Lock, ShieldCheck } from "lucide-react";
import { frApi } from "./feeRec/frUtils";
import { useSuperMode } from "../../context/SuperModeContext";
import RecommendTab from "./feeRec/RecommendTab";
import HistoryTab from "./feeRec/HistoryTab";
import PricingTab from "./feeRec/PricingTab";
import CitiesTab from "./feeRec/CitiesTab";
import CategoriesTab from "./feeRec/CategoriesTab";
import BoardsManager from "./feeRec/BoardsManager";
import ReportsTab from "./feeRec/ReportsTab";
import SettingsTab from "./feeRec/SettingsTab";

const TEAM_TABS = [
    { id: "recommend", label: "Check Fee", icon: Calculator },
    { id: "pricing", label: "Fee Structure", icon: Layers },
    { id: "history", label: "History & Approvals", icon: History },
];
const MGMT_TABS = [
    { id: "cities", label: "City Tiers", icon: MapPin },
    { id: "categories", label: "Categories", icon: Layers },
    { id: "reports", label: "Reports", icon: BarChart3 },
    { id: "settings", label: "Rules", icon: Settings2 },
];

/** V201 — Fee Recommendation System (1:1 live online classes). Independent module. */
export default function FeeRecommendationPanel({ prefill }) {
    const [boot, setBoot] = useState(null);
    const [tab, setTab] = useState("recommend");
    const load = useCallback(() => frApi.get("/bootstrap").then(setBoot).catch(() => setBoot({ error: true })), []);
    const sm = useSuperMode();
    useEffect(() => { load(); }, [load, sm.active]);
    useEffect(() => { if (boot && !boot.is_management && !TEAM_TABS.some((t) => t.id === tab)) setTab("recommend"); }, [boot, tab]);
    useEffect(() => { if (prefill) setTab("recommend"); }, [prefill]);

    if (!boot) return <div className="grid place-items-center p-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
    if (boot.error) return <div className="p-6 text-sm text-rose-700" data-testid="fee-rec-load-error">Could not load the Fee Recommendation System. Please refresh.</div>;
    const tabs = boot.is_management ? [...TEAM_TABS, ...MGMT_TABS] : TEAM_TABS;

    return (
        <div className="space-y-5 pb-24 lg:pb-0 px-3 lg:px-0 min-w-0" data-testid="fee-rec-panel">
            <header className="flex items-center gap-4 bg-[#0F172A] text-white rounded-2xl p-4 sm:p-5">
                <img src="/logo-anand-tutorials.png" alt="Anand Tutorials" className="h-10 sm:h-12 w-auto bg-white rounded-lg p-1.5" />
                <div className="min-w-0 flex-1">
                    <h2 className="font-heading text-xl sm:text-2xl font-bold" data-testid="fee-rec-title">Fee Recommendation</h2>
                    <p className="text-sm text-slate-300">1:1 Live Online Classes · approved fee ranges by city tier and course</p>
                </div>
                <span data-testid="fee-rec-role" className={`inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${boot.is_management ? "bg-emerald-400/20 text-emerald-200" : "bg-white/10 text-slate-200"}`}>
                    {boot.is_management ? <ShieldCheck className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                    {boot.is_management ? "Management" : "Team"}
                </span>
            </header>
            <nav className="flex flex-wrap gap-2" data-testid="fee-rec-tabs">
                {tabs.map((t) => (
                    <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`fee-rec-tab-${t.id}`}
                        className={`inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full text-sm font-semibold border transition-colors ${tab === t.id ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
                        <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                ))}
            </nav>
            {!boot.is_management && (
                <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="fee-rec-team-note">
                    <Lock className="w-3.5 h-3.5" /> Fee ranges, city tiers and approvals are managed by management in Super Mode.
                </p>
            )}
            {tab === "recommend" && <RecommendTab boot={boot} prefill={prefill} />}
            {tab === "history" && <HistoryTab isMgmt={boot.is_management} />}
            {tab === "pricing" && <PricingTab key={String(boot.is_management)} />}
            {tab === "cities" && <CitiesTab />}
            {tab === "categories" && <><CategoriesTab onChanged={load} /><div className="mt-4"><BoardsManager boards={boot.boards || []} onChanged={load} /></div></>}
            {tab === "reports" && <ReportsTab />}
            {tab === "settings" && <SettingsTab settings={boot.settings} onSaved={load} />}
        </div>
    );
}
